/**
 * CDB54 Calendrier+ - PWA Composable
 * Story 4.1-4.4: PWA utilities for offline detection and service worker updates
 */

import { ref, onMounted, onUnmounted } from 'vue';

export function usePwa() {
  const isOnline = ref(navigator.onLine);
  const isOffline = ref(!navigator.onLine);
  const needsRefresh = ref(false);
  const isUpdateAvailable = ref(false);

  // Event handlers
  function handleOnline() {
    isOnline.value = true;
    isOffline.value = false;
  }

  function handleOffline() {
    isOnline.value = false;
    isOffline.value = true;
  }

  // Service worker registration and update handling
  async function registerServiceWorker(): Promise<void> {
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js', {
          scope: '/',
        });

        // Check for updates
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // New content is available
                isUpdateAvailable.value = true;
                needsRefresh.value = true;
              }
            });
          }
        });

        // Handle controller change (when skipWaiting is called)
        let refreshing = false;
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          if (!refreshing) {
            refreshing = true;
            window.location.reload();
          }
        });
      } catch (error) {
        console.error('Service Worker registration failed:', error);
      }
    }
  }

  // Force update the service worker
  async function updateServiceWorker(): Promise<void> {
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration?.waiting) {
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      }
    }
  }

  // Check if app can be installed (PWA install prompt)
  const canInstall = ref(false);
  let deferredPrompt: BeforeInstallPromptEvent | null = null;

  interface BeforeInstallPromptEvent extends Event {
    prompt(): Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
  }

  function handleBeforeInstallPrompt(e: Event) {
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
    canInstall.value = true;
  }

  async function installApp(): Promise<boolean> {
    if (!deferredPrompt) return false;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    deferredPrompt = null;
    canInstall.value = false;

    return outcome === 'accepted';
  }

  onMounted(() => {
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Register service worker on mount
    registerServiceWorker();
  });

  onUnmounted(() => {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
    window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  });

  return {
    isOnline,
    isOffline,
    needsRefresh,
    isUpdateAvailable,
    canInstall,
    updateServiceWorker,
    installApp,
  };
}
